import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  SxProps,
  Theme,
  Typography,
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import { useTranslation } from 'react-i18next';
import { Link as ReactRouterLink } from 'react-router-dom';
import {
  useCoordinateSelector,
  UseCoordinateSelectorInput,
} from './useCoordinateSelector';

type CoordinateSelectorProps = UseCoordinateSelectorInput & {
  sx?: SxProps<Theme>;
};

// Keeps the arrow next to the coordinates instead of at the far right.
const summarySx: SxProps<Theme> = {
  justifyContent: 'flex-start',
  '& .MuiAccordionSummary-content': {
    flexGrow: 0,
    alignItems: 'center',
    gap: 1,
  },
};

export const CoordinateSelector = (
  props: CoordinateSelectorProps,
): JSX.Element => {
  const { sx, ...selectorInput } = props;
  const { t } = useTranslation();
  const {
    paywallLink,
    expanded,
    toggleExpanded,
    currentText,
    formats,
    copyCurrent,
    selectFormat,
  } = useCoordinateSelector(selectorInput);
  const blurFilter = paywallLink ? 'blur(3px)' : undefined;

  return (
    <Accordion
      expanded={expanded}
      onChange={paywallLink ? undefined : toggleExpanded}
      disableGutters
      elevation={0}
      slotProps={{
        heading: { component: 'div' },
        transition: { unmountOnExit: true },
      }}
      sx={sx}
    >
      {/* The copy button sits beside the summary, since a button can't nest in it. */}
      <Stack direction="row" sx={{ alignItems: 'center' }}>
        <AccordionSummary
          expandIcon={paywallLink ? undefined : <ArrowDropDownIcon />}
          sx={summarySx}
          {...(paywallLink && { component: ReactRouterLink, to: paywallLink })}
        >
          <GpsFixedIcon fontSize="small" sx={{ filter: blurFilter }} />
          <Typography
            sx={{
              fontWeight: 'fontWeightMedium',
              filter: blurFilter,
            }}
          >
            {currentText}
          </Typography>
        </AccordionSummary>

        {!paywallLink && (
          <IconButton
            onClick={copyCurrent}
            aria-label={t('coordinateSelector.copy')}
            sx={{ mr: 1, color: 'text.primary' }}
          >
            <ContentCopyIcon />
          </IconButton>
        )}
      </Stack>
      <AccordionDetails sx={{ p: 0, bgcolor: 'background.default' }}>
        <List disablePadding>
          {formats.map((formatOption) => {
            const { format, label, text } = formatOption;
            return (
              <ListItemButton
                key={format}
                onClick={() => selectFormat(formatOption)}
                sx={{ bgcolor: 'background.secondary' }}
              >
                <ListItemText
                  primary={label}
                  secondary={text}
                  slotProps={{
                    primary: { variant: 'h4', color: 'secondary' },
                    secondary: {
                      variant: 'body1',
                      color: 'textPrimary',
                      sx: { fontWeight: 'fontWeightMedium' },
                    },
                  }}
                />
                <ContentCopyIcon sx={{ color: 'text.primary' }} />
              </ListItemButton>
            );
          })}
        </List>
      </AccordionDetails>
    </Accordion>
  );
};
